import { useState } from "react";
import { motion, AnimatePresence, useReducedMotion } from "framer-motion";
import { Brain, FileText, BookOpen, LayoutDashboard } from "lucide-react";
import { useTranslation } from "@/lib/i18n";

const tabIcons = [Brain, FileText, BookOpen, LayoutDashboard];
const kbdLabels = ["⌘1", "⌘2", "⌘3", "⌘4"];

const tabCodes: Record<string, string> = {
  agentic: `session/
  CLAUDE.md          # working memory
memory/
  projects.md        # active projects
  people.md          # key contacts
  decisions.md       # decision log
skills/
  GTM-launch.md      # launch protocol
  deck-builder.md    # investor decks
  weekly-report.md   # reporting flow
protocols/
  onboarding.md      # new project setup
  review-cycle.md    # QA process`,
  content: `PIPELINE: content-production

[1] Brief intake
  → parse brief.md
  → extract key messages, audience, CTA
[2] Research layer
  → pull from memory/projects
  → cross-ref competitor data
[3] Draft generation
  → apply brand-voice.md rules
  → output: draft_v1.md
[4] Review + publish
  → human review loop
  → final export`,
  journal: `JOURNAL PROTOCOL

06:30  morning-check-in
  → energy level (1-10)
  → top 3 priorities
  → blockers from yesterday

12:00  midday-pulse
  → progress vs. plan
  → context shifts
  → decision queue

21:00  evening-review
  → completed items
  → insights captured
  → feed → memory/decisions.md`,
  "dedicated-dashboards": `DASHBOARD: gtm-launch-tracker

focus/
  tasks.md        # active tasks (9)
  blockers.md     # blockers (2)
  decisions.md    # decision log

data-streams/
  → pull: analytics.md
  → pull: socials-feed.md
  → summarise: weekly-kpis

ai-layer/
  → flag: blockers.md
  → draft: status-update.md
  → update: memory/projects.md

status: LIVE  # last sync: 08:41`,
};


function formatCodeLine(line: string) {
  if (line.trimStart().startsWith("#")) {
    const idx = line.indexOf("#");
    return (
      <>
        {line.slice(0, idx)}
        <span className="text-muted-foreground">{line.slice(idx)}</span>
      </>
    );
  }
  if (line.includes("→")) {
    return line.split("→").map((part, i, arr) =>
      i < arr.length - 1 ? (
        <span key={i}>
          {part}
          <span className="text-accent">→</span>
        </span>
      ) : (
        <span key={i}>{part}</span>
      )
    );
  }
  if (line.match(/^\s*\w+\//)) {
    const match = line.match(/^(\s*)(\w+\/)(.*)/);
    if (match) {
      return (
        <>
          {match[1]}
          <span className="text-primary">{match[2]}</span>
          {match[3]}
        </>
      );
    }
  }
  return line;
}

const AISystemsSection = () => {
  const [activeTab, setActiveTab] = useState("agentic");
  const prefersReduced = useReducedMotion();
  const t = useTranslation();
  const ai = t.aiTeams.aiSystems;

  const active = ai.tabs.find((tab) => tab.id === activeTab)!;
  const activeCode = tabCodes[activeTab];

  const anim = {
    initial: prefersReduced ? { opacity: 0 } : { opacity: 0, y: 30 },
    whileInView: { opacity: 1, y: 0 },
    viewport: { once: true, margin: "-80px" as const },
    transition: { duration: 0.6, ease: [0.16, 1, 0.3, 1] as const },
  };

  return (
    <section className="py-12 bg-card/40">
      <div className="container mx-auto px-6">
        <motion.div
          initial={anim.initial}
          whileInView={anim.whileInView}
          viewport={anim.viewport}
          transition={anim.transition}
          className="mb-10"
        >
          <p className="text-xs font-body font-semibold tracking-[0.2em] uppercase text-primary mb-3">{ai.eyebrow}</p>
          <h2 className="text-3xl sm:text-4xl font-display tracking-tight mb-4">{ai.heading}</h2>
          <p className="text-muted-foreground font-body max-w-lg text-sm">{ai.desc}</p>
        </motion.div>

        {/* Tabs */}
        <div className="flex flex-wrap gap-2 mb-8">
          {ai.tabs.map((tab, i) => {
            const Icon = tabIcons[i];
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                aria-label={tab.short}
                aria-pressed={activeTab === tab.id}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-body font-medium transition-all ${
                  activeTab === tab.id
                    ? "bg-primary text-primary-foreground"
                    : "bg-secondary text-secondary-foreground hover:bg-secondary/80"
                }`}
              >
                <Icon size={16} />
                <span className="hidden sm:inline">{tab.short}</span>
                <kbd className="hidden lg:inline font-mono text-[10px] px-1.5 py-0.5 rounded border border-border bg-secondary text-muted-foreground ml-1">
                  {kbdLabels[i]}
                </kbd>
              </button>
            );
          })}
        </div>

        {/* Content */}
        <AnimatePresence mode="wait">
          <motion.div
            key={activeTab}
            initial={prefersReduced ? { opacity: 0 } : { clipPath: "inset(0 100% 0 0)", opacity: 1 }}
            animate={prefersReduced ? { opacity: 1 } : { clipPath: "inset(0 0 0 0)", opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={prefersReduced ? { duration: 0.2 } : { duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
            className="grid lg:grid-cols-2 gap-6"
          >
            <div>
              <p className="text-xs font-body font-semibold tracking-widest text-primary mb-2">{active.label}</p>
              <p className="text-muted-foreground font-body text-sm leading-relaxed mb-5">{active.description}</p>
              <ul className="space-y-2.5">
                {active.bullets.map((b, i) => (
                  <li key={i} className="flex gap-3 text-sm font-body text-muted-foreground leading-relaxed">
                    <span className="w-1.5 h-1.5 rounded-full bg-primary flex-shrink-0 mt-1.5" />
                    {b}
                  </li>
                ))}
              </ul>
            </div>
            <div>
              {/* Terminal header */}
              <div className="flex items-center gap-1.5 pb-3 mb-0 px-4 pt-3 bg-background/80 rounded-t-lg border border-b-0 border-border/50">
                <span className="w-2 h-2 rounded-full bg-red-500/60" />
                <span className="w-2 h-2 rounded-full bg-yellow-500/60" />
                <span className="w-2 h-2 rounded-full bg-green-500/60" />
                <span className="text-xs font-mono text-muted-foreground ml-2">{active.filename}</span>
              </div>
              <div className="code-block rounded-t-none text-xs leading-relaxed whitespace-pre font-mono text-muted-foreground border-t border-border/30">
                {activeCode.split("\n").map((line, i) => (
                  <div key={i}>{formatCodeLine(line)}</div>
                ))}
                <span className="text-primary animate-blink">|</span>
              </div>
            </div>
          </motion.div>
        </AnimatePresence>
      </div>
    </section>
  );
};

export default AISystemsSection;
